import React, { useMemo } from 'react';
import { Breadcrumb } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import { Link, useLocation, matchPath } from 'react-router-dom';
import { ROUTE_META, VIRTUAL_NODES, ROOT_PATTERN } from '../variables/routeMeta';

/* Index pattern → meta, pour résolution des parents en O(1). */
const META_BY_PATTERN = ROUTE_META.reduce((acc, m) => {
  acc[m.pattern] = m;
  return acc;
}, {});

/* Routes triées par spécificité : on essaie d'abord les routes statiques
 * (moins de segments dynamiques), puis les plus longues. Ainsi
 * « /sinistres/nouveau » l'emporte sur « /sinistres/:id ». */
const SORTED_PATTERNS = [...ROUTE_META].sort((a, b) => {
  const pa = (a.pattern.match(/:/g) || []).length;
  const pb = (b.pattern.match(/:/g) || []).length;
  if (pa !== pb) return pa - pb;
  return b.pattern.length - a.pattern.length;
});

/* Remplace les segments dynamiques (:id) par les valeurs réelles des params. */
function fillPattern(pattern, params) {
  return pattern.replace(/:([A-Za-z0-9_]+)/g, (_, key) =>
    params[key] != null ? params[key] : `:${key}`,
  );
}

/* Trouve l'entrée de route correspondant au pathname courant. */
function matchRoute(pathname) {
  for (const entry of SORTED_PATTERNS) {
    const m = matchPath({ path: entry.pattern, end: true }, pathname);
    if (m) return { entry, params: m.params || {} };
  }
  return null;
}

/* Résout une référence de parent (pattern de route ou nœud virtuel '#…'). */
function resolveParent(ref) {
  if (!ref) return null;
  if (ref.startsWith('#')) {
    const v = VIRTUAL_NODES[ref];
    return v ? { entry: { pattern: ref, label: v.label, parent: v.parent, virtual: true } } : null;
  }
  const m = META_BY_PATTERN[ref];
  return m ? { entry: m } : null;
}

/**
 * Fil d'Ariane automatique, construit à partir du chemin courant et du
 * registre `routeMeta`. Aucune configuration par page n'est requise.
 */
export default function Breadcrumbs() {
  const location = useLocation();

  const trail = useMemo(() => {
    const matched = matchRoute(location.pathname);
    if (!matched) return [];

    const params = matched.params;
    const items = [];
    let node = { entry: matched.entry };
    const guard = new Set(); // anti-boucle de sécurité

    while (node && node.entry && !guard.has(node.entry.pattern)) {
      guard.add(node.entry.pattern);
      const { pattern, label, virtual } = node.entry;
      items.unshift({
        key: pattern,
        label,
        // Les nœuds virtuels (rubriques) ne sont pas cliquables.
        to: virtual ? null : fillPattern(pattern, params),
        isRoot: pattern === ROOT_PATTERN,
      });
      node = resolveParent(node.entry.parent);
    }
    return items;
  }, [location.pathname]);

  // Rien à afficher pour la racine seule (Tableau de bord).
  if (trail.length <= 1) return null;

  const items = trail.map((c, idx) => {
    const isLast = idx === trail.length - 1;
    const content = c.isRoot ? (
      <>
        <HomeOutlined style={{ marginRight: 4 }} />
        {c.label}
      </>
    ) : (
      c.label
    );

    // Dernier élément = page courante : non cliquable.
    if (isLast || !c.to) {
      return { key: c.key, title: <span style={{ color: '#155731', fontWeight: 600 }}>{content}</span> };
    }
    return { key: c.key, title: <Link to={c.to}>{content}</Link> };
  });

  return (
    <Breadcrumb
      items={items}
      style={{
        marginBottom: 18,
        fontSize: 13,
        fontFamily: "'DM Sans', sans-serif",
      }}
    />
  );
}
